import React from "react";
import moment from "moment";
import {Link, useHistory, useLocation, useParams} from 'react-router-dom'
import {cn} from "../../utils/cn";
import Popup from "../../components/popup/popup";
import {StoreContext} from '../../store'
import {dateToString} from '../../utils/date'
import {prettyAmount} from '../../utils/amount'
import {PayPalBtn, PayPalOneTime} from "./paypal";
import Spinner from '../../components/spinner/spinner'
import usePromo from "../../hooks/usePromo";
import useQuora from '../../hooks/useQuora'
import useApi, {CONTROLLER} from '../../hooks/useApi'
import {trackGooglePayment, withGoogleAdsAttribution} from '../../utils/googleConversions';

const isProd = process.env.NODE_ENV === 'production';

export const stripeCheckoutIsEnabled = (value) => value === '1';

const stripeCheckoutEnabled = stripeCheckoutIsEnabled(process.env.REACT_APP_ENABLE_STRIPE_CHECKOUT);

export const stripeCheckoutCanBeShown = (clientEnabled, serverAvailable) => (
  clientEnabled && serverAvailable === true
);

export const checkoutProviderVisibility = (clientEnabled, serverAvailable) => ({
  showLegacyCheckout: !clientEnabled || serverAvailable !== true,
  showStripeCheckout: stripeCheckoutCanBeShown(clientEnabled, serverAvailable),
});

export const buildStripeCheckoutPayload = ({planId, quantity, promo}) => {
  const payload = {
    planId: Number(planId),
    quantity: Number(quantity),
  };
  const normalizedPromo = String(promo || '').trim();
  if (normalizedPromo) {
    payload.promo = normalizedPromo;
  }
  return payload;
};

export const stripeCheckoutUrl = (response) => {
  const url = response?.data?.url;
  if (typeof url !== 'string' || !url.trim()) return null;
  try {
    const parsed = new URL(url);
    return parsed.protocol === 'https:' && parsed.hostname === 'checkout.stripe.com'
      ? parsed.href
      : null;
  } catch (error) {
    return null;
  }
};

const Order = () => {
  const {push} = useHistory();
  const {search} = useLocation();
  const {planId} = useParams();
  const {store: {username, email}} = React.useContext(StoreContext);
  const track = useQuora(email);
  const isApiCreditPack = Number(planId) === 15;
  const initialQty = React.useMemo(() => {
    const raw = new window.URLSearchParams(search).get('qty');
    const num = Number(raw);
    const parsed = Number.isFinite(num) ? Math.max(1, Math.floor(num)) : 1;
    return Math.min(parsed, 20);
  }, [search]);
  const [qty, setQty] = React.useState(initialQty);
  const {payload: paypalPayload, execute: paypalExecute} = useApi(CONTROLLER.PAYPAL);
  const {loading: stripeLoading, execute: stripeExecute} = useApi(CONTROLLER.STRIPE_CHECKOUT);
  const {loading: planLoading, payload: planPayload, execute: planExecute} = useApi(CONTROLLER.PLAN);
  const [state, setState] = React.useState({show: false, message: "", title: ""});
  const {promo, setPromo, isPromo, isNotPromo, loading, data: promoData} = usePromo(planId);

  React.useEffect(() => {
    setQty(initialQty);
  }, [initialQty]);

  React.useEffect(() => {
    planExecute(undefined, {urlParams: {planId}});
  }, [planExecute, planId]);

  React.useEffect(() => () => {
    for (const item of document.querySelectorAll('body script[src*="www.paypal.com/sdk"]')) {
      for (const param in window) {
        if (window?.hasOwnProperty(param) && param.endsWith(item.dataset?.uidAuto)) {
          delete window[param];
        }
      }
      item.remove();
    }
    window.paypal = undefined;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  React.useEffect(() => {
    if (planPayload) {
      const unitAmount = Number(planPayload.data.Amount) || 0;
      const totalAmount = isApiCreditPack ? unitAmount * qty : unitAmount;
      // --- facebook ---
      window.fbq && window.fbq('track', 'InitiateCheckout');
      // --- google ---
      window.gtag && window.gtag('event', 'begin_checkout', {
        event_category: 'ecommerce',
        affiliation: 'deeptrading.ai',
        value: totalAmount,
        currency: 'USD',
        tax: 0,
        shipping: 0,
        items: [{
          id: (`${planPayload.data.Type} ${planPayload.data.Name}` || '').replaceAll(/\s+/g, ''),
          name: `${planPayload.data.Type} ${planPayload.data.Name}`,
          quantity: isApiCreditPack ? qty : 1,
          price: unitAmount,
        }],
      });
    }
  }, [isApiCreditPack, planPayload, qty]);


  const paypalSubscribe = async (data, {subscription}) => subscription.create({
    plan_id: (isPromo && promoData?.planId) || (isProd ? planPayload.data.PaypalPlanId : planPayload.data.PaypalPlanIdTest),
    custom_id: `${username}::${planId}::${planPayload.data.Amount}::${new Date().getTime()}`,
    application_context: {
      shipping_preference: "NO_SHIPPING",
    },
  });

  const paypalCreateOrder = (data, {order}) => order.create({
    purchase_units: [{
      amount: {
        currency_code: "USD",
        value: ((Number(planPayload.data.Amount) || 0) * (isApiCreditPack ? qty : 1)).toFixed(2),
      },
      custom_id: `${username}::${planId}::qty=${isApiCreditPack ? qty : 1}::${new Date().getTime()}`,
    }],
    application_context: {
      shipping_preference: "NO_SHIPPING",
    },
  });


  const paypalOnApprove = async (data) => {
    const {success, measurement} = await paypalExecute(await withGoogleAdsAttribution({...data, promo}));
    if (success) {
      setState({
        show: true,
        title: "Payment Confirmation",
        message: measurement?.status === 'paid'
          ? `Your first subscription payment of $${prettyAmount(measurement.value)} was confirmed.`
          : measurement?.status === 'trial'
            ? 'Your subscription trial is active. Review the renewal date and amount in Orders.'
            : 'Your subscription was created. Payment confirmation is pending.',
      });
      track();
      // --- twitter ---
      window.twq && window.twq('event', 'tw-o9bb4-od1ee', {
        value: planPayload.data.Amount,
        currency: 'USD',
        conversion_id: data.subscriptionID,
      });
      // --- facebook ---
      window.fbq && window.fbq('track', 'Subscribe', {
        subscription_id: data.subscriptionID,
        value: planPayload.data.Amount,
        currency: 'USD',
      });
      isPromo && window.fbq && window.fbq('track', 'StartTrial', {
        subscription_id: data.subscriptionID,
        value: planPayload.data.Amount,
        currency: 'USD',
      });
      // Use only server-confirmed payment metadata for Google conversions.
      trackGooglePayment(measurement);
    } else {
      setState({
        show: true,
        title: "Payment Confirmation",
        message: `Something went wrong.`,
      });
    }
  };

  const paypalOneTimeOnApprove = async (data) => {
    const {success, measurement} = await paypalExecute(data);
    if (success) {
      const unitAmount = Number(planPayload.data.Amount) || 0;
      const totalAmount = isApiCreditPack ? unitAmount * qty : unitAmount;
      setState({
        show: true,
        title: "Payment Confirmation",
        message: `Your one time payment of $${prettyAmount(totalAmount)} was successful.`,
      });
      track();
      // --- facebook ---
      window.fbq && window.fbq('track', 'Purchase', {value: totalAmount, currency: 'USD'});
      // Use only server-confirmed payment metadata for Google conversions.
      trackGooglePayment(measurement);
    } else {
      setState({
        show: true,
        title: "Payment Confirmation",
        message: `Something went wrong.`,
      });
    }
  };

  const openStripeCheckout = async () => {
    const response = await stripeExecute(buildStripeCheckoutPayload({
      planId: planPayload.data.Id,
      quantity: isApiCreditPack ? qty : 1,
      promo,
    }));
    const url = stripeCheckoutUrl(response);
    if (response?.success && url) {
      window.location.assign(url);
      return;
    }
    setState({
      show: true,
      title: "Secure Checkout",
      message: response?.message || "Unable to open secure checkout.",
    });
  };

  const handleClose = () => {
    setState({
      show: false,
      title: "",
      message: "",
    });

    if (paypalPayload?.success) {
      push("/cabinet/orders");
    }
  };

  if (planLoading || !planPayload?.data) {
    return <Spinner/>
  }

  const checkoutParams = new window.URLSearchParams();
  if (isPromo) {
    checkoutParams.set('promo', promo);
  }
  if (isApiCreditPack) {
    checkoutParams.set('qty', String(qty));
  }
  const checkoutQuery = checkoutParams.toString();
  const checkoutUrl = `/cabinet/checkout/${planPayload.data.Id}${checkoutQuery ? `?${checkoutQuery}` : ``}`;
  const unitAmount = Number(planPayload.data.Amount) || 0;
  const totalAmount = isApiCreditPack ? unitAmount * qty : unitAmount;
  const providerVisibility = checkoutProviderVisibility(
    stripeCheckoutEnabled,
    planPayload.data.stripeCheckoutAvailable,
  );

  return (
    <section className="dashboardSec">
      <div className="dashOrdSec">
        <div className="container">
          <div className="row">
            <div className="col-12 col-sm-12 col-md-12">
              <h2>
                <span>Review your Order</span>
              </h2>
            </div>
          </div>
          <div className="row">
            <div className="col-12 col-sm-12 col-md-12 col-lg-12 col-xl-12">
              <div className="orderTable">
                <table className="table">
                  <thead>
                  <tr>
                    <th valign="middle">Order date</th>
                    <th valign="middle">Product</th>
                    <th valign="middle">Payment</th>
                    <th valign="middle">Expires</th>
                    <th valign="middle">Amount</th>
                  </tr>
                  </thead>

                  <tbody>
                  <tr>
                    <td>
                      {dateToString()}
                    </td>
                    <td style={{textTransform: "capitalize"}}>
                      {planPayload.data.Name}
                    </td>
                    <td>
                      {planPayload.data.Type}
                    </td>
                    <td>
                      {planPayload.data.Type === "Monthly" && moment().add(1, "months").format("MMMM Do, YYYY")}
                      {planPayload.data.Type === "Yearly" && moment().add(1, "years").format("MMMM Do, YYYY")}
                      {planPayload.data.Type === "One Time" && moment().add(7, "days").format("MMMM Do, YYYY")}
                    </td>
                    <td>
                      ${prettyAmount(totalAmount)}{isApiCreditPack ? ` (qty ${qty})` : ``}
                    </td>
                  </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
          {planPayload.data.Type === 'One Time' && isApiCreditPack && (
            <div className="row">
              <div className="col-12 col-sm-12 col-md-12 col-lg-12 col-xl-12">
                <div style={{maxWidth: 260, marginLeft: 'auto', marginBottom: 10}}>
                  <label htmlFor="apiPackQty" style={{color: '#fff', display: 'block', marginBottom: 6}}>
                    Quantity ($5 each)
                  </label>
                  <input
                    id="apiPackQty"
                    type="number"
                    className="form-control"
                    min={1}
                    max={20}
                    value={qty}
                    onChange={(e) => {
                      const next = Math.floor(Number(e.target.value) || 1);
                      setQty(Math.max(1, Math.min(20, next)));
                    }}
                  />
                </div>
              </div>
            </div>
          )}
          <div className="row">
            {providerVisibility.showLegacyCheckout && (
              <div style={{marginTop: 27}} className="col-12 col-sm-12 col-md-4 col-lg-4 col-xl-3">
                {planPayload.data.Type === 'One Time' ? (
                  <PayPalOneTime
                    createOrder={paypalCreateOrder}
                    onApprove={paypalOneTimeOnApprove}
                  />
                ) : (
                  <PayPalBtn
                    amount={planPayload.data.Amount}
                    createSubscription={paypalSubscribe}
                    onApprove={paypalOnApprove}
                  />
                )}
              </div>
            )}
            <div className="col-12 col-sm-12 col-md-8 col-lg-8 col-xl-4">
              {providerVisibility.showStripeCheckout ? (
                <button
                  type="button"
                  className="btn btn-primary btn-block"
                  style={{marginTop: "3%", minHeight: 48}}
                  onClick={openStripeCheckout}
                  disabled={stripeLoading}
                >
                  {stripeLoading ? 'Opening secure checkout...' : 'Pay with card or Link'}
                </button>
              ) : providerVisibility.showLegacyCheckout ? (
                <Link
                  style={{marginTop: "3%"}}
                  to={checkoutUrl}
                >
                  {['Visa', 'Mastercard', 'Amex', 'Discover'].map((img) => (
                    <img
                      id={img}
                      alt={img}
                      key={img}
                      src={`/images/${img}.png`}
                      style={{width: "70px", marginLeft: 10}}
                    />
                  ))}
                </Link>
              ) : (
                <div style={{marginTop: "3%"}}>
                  Secure checkout is temporarily unavailable.
                </div>
              )}
            </div>
            <div className="col-12 col-sm-12 col-md-8 col-lg-8 col-xl-5">
              <div
                className="form-group row"
                style={{marginTop: '15px', maxWidth: '450px', marginLeft: 'auto', height: '85px'}}
              >
                <label htmlFor="promo" className="col-sm-4 col-form-label" style={{textAlign: 'right'}}>
                  Promo Code:
                </label>
                <div className="col-sm-8">
                  <div className="form-group position-relative">
                    <input
                      type="text"
                      className={cn(
                        "form-control",
                        isPromo === true && 'is-valid',
                        isNotPromo === true && 'is-invalid',
                      )}
                      id="promo"
                      style={{textTransform: 'uppercase'}}
                      value={promo}
                      onChange={(e) => setPromo(e.target.value)}
                      maxLength="8"
                      readOnly={loading}
                    />
                    <div className="valid-feedback feedback-icon"/>
                    <div className="invalid-feedback feedback-icon"/>
                  </div>
                  {isPromo && (
                    <div className="valid-feedback d-block">
                      {promoData?.message}
                    </div>
                  )}
                  {isNotPromo && (
                    <div className="invalid-feedback d-block">
                      {promoData}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <Popup
        {...state}
        handleClose={handleClose}
      />
    </section>
  );
}

export default Order;
